import { getChars, prefetchChars } from "../lib/charsCache";
import TermsGate from "./TermsGate";
import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { useOnlinePlayers } from "../lib/useOnlinePlayers";
import { api } from "../lib/api";
import { SOCIALS, GAME_ORIGIN } from "../lib/siteConfig";
import { User, LogOut, Crown, LogIn, ChevronDown, Check, Play, Loader2, Menu, X, Instagram, Facebook, Youtube, Scale } from "lucide-react";
import { toast } from "sonner";
import { LEGAL_ENTITY } from "../lib/legalConfig";

const LANGUAGES = [
  { code: "pt", label: "Português", flag: "br" },
  { code: "en", label: "English", flag: "us" },
  { code: "es", label: "Español", flag: "es" },
  { code: "pl", label: "Polski", flag: "pl" },
  { code: "fr", label: "Français", flag: "fr" },
];

const DiscordIcon = ({ size = 18 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor"><path d="M20.3 4.4A19.8 19.8 0 0 0 15.4 3l-.2.4a18.3 18.3 0 0 1 4.3 1.6 17.4 17.4 0 0 0-15 0A18.3 18.3 0 0 1 8.8 3.4L8.600 3a19.8 19.8 0 0 0-4.900 1.400C.7 8.900-.1 13.300.3 17.600a19.9 19.9 0 0 0 6 3l1.300-2.100a13 13 0 0 1-2-1l.5-.4a14.200 14.200 0 0 0 12.200 0l.5.400a13 13 0 0 1-2 1l1.300 2.100a19.900 19.900 0 0 0 6-3c.5-5-.8-9.400-3.800-13.200zM8.500 15c-1.200 0-2.100-1-2.100-2.300s.9-2.300 2.100-2.300 2.100 1 2.100 2.300-.9 2.300-2.100 2.300zm7 0c-1.200 0-2.100-1-2.100-2.300s.9-2.300 2.100-2.300 2.100 1 2.100 2.300-.9 2.300-2.100 2.300z" /></svg>
);
const SOCIAL_ICON = { instagram: <Instagram size={18} />, facebook: <Facebook size={18} />, youtube: <Youtube size={18} />, discord: <DiscordIcon /> };

const L = {
  home: { pt: "Início", en: "Home", es: "Inicio", pl: "Start", fr: "Accueil" },
  roadmap: { pt: "Roadmap", en: "Roadmap", es: "Roadmap", pl: "Roadmap", fr: "Roadmap" },
  ranking: { pt: "Ranking", en: "Ranking", es: "Ranking", pl: "Ranking", fr: "Classement" },
  rules: { pt: "Regras", en: "Rules", es: "Reglas", pl: "Zasady", fr: "Règles" },
  wiki: { pt: "Wiki", en: "Wiki", es: "Wiki", pl: "Wiki", fr: "Wiki" },
  bazar: { pt: "RMT Bazar", en: "RMT Bazaar", es: "RMT Bazar", pl: "RMT Bazar", fr: "RMT Bazar" },
  login: { pt: "Entrar", en: "Login", es: "Entrar", pl: "Zaloguj", fr: "Connexion" },
  online: { pt: "Online", en: "Online", es: "Online", pl: "Online", fr: "En ligne" },
};

export default function Layout({ children }) {
  const { account, logout } = useAuth();
  const { lang, setLang } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const { online, status: onlineStatus } = useOnlinePlayers();
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [loadingPlay, setLoadingPlay] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [isGm, setIsGm] = useState(false);   // conta liberada para o Painel do GM (o servidor responde 403 para quem não é)
  const langRef = useRef(null);
  const tr = (k) => (L[k] && (L[k][lang] || L[k].pt)) || k;

  const handlePlayGame = async () => {
    setDrawer(false);
    if (!account) { toast.error("Você precisa estar logado na sua conta para jogar PKX_IDLE!"); navigate("/login"); return; }
    setLoadingPlay(true);
    try {
      const characters = await getChars();
      if (characters.length === 0) { toast.info("Você precisa criar um personagem antes de entrar no jogo!"); navigate("/panel"); return; }
      if (characters.length === 1) {
        const char = characters[0];
        const ticketRes = await api.post("/game/launch-ticket", { character_id: char.id });
        if (ticketRes.data?.success && ticketRes.data?.ticket) {
          toast.success(`Iniciando PKX_IDLE com ${char.name}...`);
          { const __u = `${GAME_ORIGIN}/?ticket=${encodeURIComponent(ticketRes.data.ticket)}&lang=${encodeURIComponent(localStorage.getItem("lang") || "pt")}`; const __w = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) ? null : window.open(__u, "_blank"); if (!__w) window.location.href = __u; }
          return;
        }
      }
      toast.info("Selecione seu personagem para jogar!");
      navigate("/panel");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao conectar com o jogo.");
      navigate("/panel");
    } finally { setLoadingPlay(false); }
  };

  useEffect(() => {
    const h = (e) => { if (langRef.current && !langRef.current.contains(e.target)) setIsLangOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  useEffect(() => { if (account) prefetchChars(); }, [account]);
  useEffect(() => {
    if (!account) { setIsGm(false); return undefined; }
    let off = false;
    api.get("/gm/me").then((r) => { if (!off) setIsGm(!!(r.data && r.data.gm)); }).catch(() => { if (!off) setIsGm(false); });
    return () => { off = true; };
  }, [account]);
  useEffect(() => { setDrawer(false); window.scrollTo({ top: 0 }); }, [location.pathname]);

  const path = location.pathname;
  const newLook = path === "/" || path.startsWith("/roadmap") || path.startsWith("/ranking") || path.startsWith("/capturas") || path.startsWith("/legal") || path.startsWith("/wiki");
  const fullWidth = path.startsWith("/panel") || path.startsWith("/game") || path.startsWith("/admin");
  const links = [
    { label: tr("home"), to: "/", end: true }, { label: tr("roadmap"), to: "/roadmap" }, { label: tr("ranking"), to: "/ranking" }, { label: "Capturas", to: "/capturas" },
    { label: tr("rules"), to: "/legal" }, { label: tr("wiki"), to: "/wiki" }, { label: tr("bazar"), to: "/rmt-marketplace" },
  ];
  const cur = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];
  const linkCls = ({ isActive }) => `nav-link ${isActive ? "active" : ""}`;

  return (
    <div className={`pkx-skin ${path.startsWith("/panel") ? "page-panel" : ""} min-h-screen flex flex-col relative overflow-x-hidden`}>
      <a href="#conteudo" className="skip-link">Pular para o conteúdo</a>
      <TermsGate />
      {!fullWidth && <aside className="social-rail">
        {SOCIALS.map((s) => (
          <a key={s.id} href={s.url || "#"} target={s.url ? "_blank" : undefined} rel="noreferrer" aria-label={s.label} onClick={(e) => { if (!s.url) e.preventDefault(); }}>
            {SOCIAL_ICON[s.id]}<span>{s.label}</span>
          </a>
        ))}
      </aside>}

      <header className="nav-wrap">
        <div className="nav-bar">
          <nav className="nav-links desk">
            {links.map((it) => <NavLink key={it.to} to={it.to} end={it.end} className={linkCls}>{it.label}</NavLink>)}
          </nav>
          <button className="nav-burger" onClick={() => setDrawer(true)} aria-label="Menu"><Menu size={20} /></button>

          <Link to="/" className="nav-logo" aria-label="PKX Idle">
            <img src="/images/logo-pkx-sm.png" srcSet="/images/logo-pkx-sm.png 1x, /images/logo-pkx.png 2x" alt="PKX Idle" />
          </Link>

          <div className="nav-right">
            <div className={`nav-status ${onlineStatus === "offline" ? "off" : ""}`}><i /><span>{onlineStatus === "offline" ? "Servidor offline" : `${online ?? 0} jogando`}</span></div>

            <div className="relative" ref={langRef}>
              <button onClick={() => setIsLangOpen(!isLangOpen)} className="pkx-chip cursor-pointer hover:border-[var(--line-hot)] transition-colors" title="Idioma">
                <img src={`https://flagcdn.com/w40/${cur.flag}.png`} className="w-5 h-3.5 object-cover rounded-sm" alt={cur.code} />
                <span>{cur.code.toUpperCase()}</span>
                <ChevronDown size={14} className={`transition-transform ${isLangOpen ? "rotate-180" : ""}`} />
              </button>
              {isLangOpen && (
                <div className="absolute right-0 top-full mt-3 w-56 pkx-card !overflow-visible p-2 z-50">
                  {LANGUAGES.map((item) => (
                    <button key={item.code} onClick={() => { setLang(item.code); setIsLangOpen(false); }}
                      className={`w-full flex items-center justify-between p-2.5 rounded-xl text-left text-sm font-bold transition-colors ${lang === item.code ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5 hover:text-white"}`}>
                      <span className="flex items-center gap-3"><img src={`https://flagcdn.com/w40/${item.flag}.png`} className="w-6 h-4 object-cover rounded-sm" alt="" />{item.label}</span>
                      {lang === item.code && <Check size={16} className="text-emerald-400" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button onClick={handlePlayGame} disabled={loadingPlay} className="pkx-btn primary sm" title="Jogar PKX_IDLE">
              {loadingPlay ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} className="fill-white" />}<span>Jogar</span>
            </button>

            {account ? (
              <>
                {isGm && <Link to="/gm" className="pkx-chip !px-3.5 !py-2 !border-amber-400/50 !text-amber-300" title="Painel do GM (balanceamento)"><Scale size={14} /><span className="nav-gm-label">Painel GM</span></Link>}
                <Link to="/panel" className={`pkx-chip !px-3.5 !py-2 ${account.role === "admin" ? "!border-amber-400/50 !text-amber-300" : ""}`}>
                  {account.role === "admin" ? <Crown size={14} className="text-amber-400 fill-amber-400" /> : <User size={14} className="text-[var(--r-hot)]" />}
                  <span className="max-w-[110px] truncate">{account.username}</span>
                </Link>
                <button onClick={() => { logout(); navigate("/"); }} className="p-2 text-white/40 hover:text-red-400 transition-colors" title="Sair"><LogOut size={17} /></button>
              </>
            ) : (
              <Link to="/login" className="pkx-btn ghost sm"><LogIn size={14} strokeWidth={3} /><span>{tr("login")}</span></Link>
            )}
          </div>
        </div>
      </header>

      {drawer && (
        <div className="nav-drawer">
          <button className="absolute top-6 right-6 nav-burger !flex" onClick={() => setDrawer(false)}><X size={20} /></button>
          {links.map((it) => <NavLink key={it.to} to={it.to} end={it.end} className={linkCls}>{it.label}</NavLink>)}
          <div className="flex gap-3 mt-4"><button onClick={handlePlayGame} className="pkx-btn primary flex-1"><Play size={14} className="fill-white" />Jogar</button>
            {!account && <Link to="/login" className="pkx-btn ghost flex-1">{tr("login")}</Link>}</div>
          {account && <>{isGm && <Link to="/gm" className="nav-link" onClick={() => setDrawer(false)}>Painel do GM</Link>}<Link to="/panel" className="nav-link">Meu painel</Link><button onClick={() => { logout(); setDrawer(false); navigate("/"); }} className="nav-link text-left">Sair</button></>}
          <div className="flex flex-wrap gap-2 mt-3">{LANGUAGES.map((l) => <button key={l.code} onClick={() => { setLang(l.code); }} className={`pkx-chip cursor-pointer ${lang === l.code ? "!border-[var(--line-hot)]" : ""}`}><img src={`https://flagcdn.com/w40/${l.flag}.png`} className="w-5 h-3.5 object-cover rounded-sm" alt="" />{l.code.toUpperCase()}</button>)}</div>
        </div>
      )}

      <main id="conteudo" tabIndex={-1} className={`flex-1 relative z-10 ${newLook ? "" : fullWidth ? "w-full max-w-[1600px] mx-auto px-6 pt-[112px] pb-12" : "w-full max-w-[1280px] mx-auto px-6 pt-[120px] pb-12"}`}>
        {children}
      </main>

      <footer className="pkx-footer relative z-10">
        <div className="pkx-wrap">
          <div className="foot-grid">
            <div>
              <img src="/images/logo-pkx-sm.png" alt="PKX Idle" className="h-20 w-auto" style={{ filter: "drop-shadow(0 6px 18px rgba(255,31,61,.55))" }} />
              <p className="text-sm text-[#bfaeb3] leading-relaxed mt-4 max-w-[300px]">Um MMORPG de Pokémon que roda no navegador, com caça automática, economia própria e uma comunidade que decide o rumo do jogo.</p>
            </div>
            <div><h5>Navegação</h5><Link to="/">{tr("home")}</Link><Link to="/roadmap">Roadmap</Link><Link to="/ranking">{tr("ranking")}</Link><Link to="/legal">{tr("rules")}</Link><Link to="/privacidade">Privacidade</Link><Link to="/reembolso">Reembolso</Link></div>
            <div><h5>Wiki</h5><Link to="/wiki/bestiario">Bestiário</Link><Link to="/wiki/itens">Itens</Link><Link to="/wiki/guias">Guias</Link><Link to="/rmt-marketplace">RMT Bazar</Link><Link to="/support">Suporte</Link></div>
            <div><h5>Redes sociais</h5><p className="text-sm text-[#bfaeb3] mb-3">Siga e fique por dentro das novidades.</p>
              <div className="foot-soc">{SOCIALS.map((s) => <a key={s.id} href={s.url || "#"} target={s.url ? "_blank" : undefined} rel="noreferrer" aria-label={s.label} onClick={(e) => { if (!s.url) e.preventDefault(); }}>{SOCIAL_ICON[s.id]}</a>)}</div></div>
          </div>
          <div className="border-t border-white/10 py-6 text-center text-xs text-[#8f7d83]">
            Pokémon e seus personagens são marcas de Nintendo, Creatures Inc. e GAME FREAK inc. PKX Idle é um projeto independente de fãs, sem vínculo com essas empresas. · PKX Idle © {new Date().getFullYear()}
            {(LEGAL_ENTITY.nome || LEGAL_ENTITY.documento || LEGAL_ENTITY.email) && <div className="mt-2">{[LEGAL_ENTITY.nome, LEGAL_ENTITY.documento, LEGAL_ENTITY.email, LEGAL_ENTITY.endereco].filter(Boolean).join(" · ")}</div>}
          </div>
        </div>
      </footer>
    </div>
  );
}
